/**
 * ClinicalEvaluator — Orquesta PatternMatcher + TriageClassifier sobre el
 * paciente actual y publica resultados al state.
 *
 * Es un wrapper delgado sobre los dos motores para que main.ts no tenga que
 * conocer sus APIs internas. La re-evaluación reactiva (ante FINDING_REVEALED
 * / VITAL_CHANGED) sigue siendo responsabilidad de ReasoningEngine, que
 * vive en el bus de eventos. Esta clase cubre el camino sincrónico: snapshot
 * inicial al cargar paciente y llamadas explícitas tras órdenes de exámenes.
 */

import state from '../core/state';
import { PatternMatcher } from './PatternMatcher';
import { TriageClassifier } from './TriageClassifier';
import { Patient } from './Patient';

const CASE_IDS = ['iam', 'sepsis', 'epoc'] as const;
export type BaseCaseId = (typeof CASE_IDS)[number];

export class ClinicalEvaluator {
  private patient: Patient | null = null;

  constructor(
    private patternMatcher: PatternMatcher,
    private triageClassifier: TriageClassifier
  ) {}

  setPatient(patient: Patient | null): void {
    this.patient = patient;
  }

  /**
   * Evalúa patrones y re-clasifica triage sobre el snapshot actual del
   * paciente. Los hallazgos detectados se publican vía state.addDetectedPattern
   * y los cambios de triage vía state.updatePatientPriority.
   */
  evaluate(): void {
    if (!this.patient) return;

    const nodes = Array.from(this.patient.nodes.values()).map(n => n.data);

    const detected = this.patternMatcher.evaluate(nodes);
    detected.forEach(pattern => state.addDetectedPattern(pattern));

    const assessment = this.triageClassifier.classify(this.patient.data, 'auto');
    if (assessment.priority !== this.patient.data.triagePriority) {
      state.updatePatientPriority(assessment.priority);
    }
  }

  /**
   * Heurística para mapear patient.id → caseId de las tablas de datos
   * (differential-data, deterioration-data). Los ids pueden ser "CASE-IAM",
   * "IAM-001", "iam-gen-3847", etc. Devuelve null si no hay match.
   */
  static inferCaseId(patientId: string): BaseCaseId | null {
    const id = patientId.toLowerCase();
    return CASE_IDS.find(c => id.includes(c)) ?? null;
  }

  /**
   * Extrae el caseId base de un scenarioId posiblemente generado.
   * "iam-gen-3847" → "iam". "iam" → "iam". Los casos generados heredan las
   * tablas del caso base.
   */
  static baseCaseId(scenarioId: string): string {
    const lower = scenarioId.toLowerCase();
    return CASE_IDS.find(b => lower.startsWith(b)) ?? scenarioId;
  }
}

export default ClinicalEvaluator;
